import { Component, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalculationPdfExportService } from '../../services/calculation-pdf-export.service';
import { CalculationStorageService, type CalculationListItem } from '../../services/calculation-storage.service';
import { SubscriptionService } from '../../services/subscription.service';


@Component({
  selector: 'app-export-comparison-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    @if (isOpen()) {
      <div class="dialog-overlay" (click)="close()">
        <div class="dialog-content" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <h2>Vergleichs-Export</h2>
            <button class="close-btn" (click)="close()" aria-label="Schließen">&times;</button>
          </div>
          
          <div class="dialog-body">
            <p class="info-text">
              Wählen Sie 2 bis 3 Berechnungen zum Vergleichen aus.
            </p>

            <div class="calculations-list">
              @for (calc of calculationsList(); track calc.calculationId) {
                <div class="calc-item">
                  <label class="checkbox-label">
                    <input 
                      type="checkbox" 
                      [checked]="isSelected(calc.calculationId)"
                      (change)="toggleSelection(calc.calculationId)"
                      [disabled]="isAtMaxSelection() && !isSelected(calc.calculationId)"
                    />
                    <span class="calc-name">{{ calc.name }}</span>
                    @if (calc.city || calc.address) {
                      <span class="calc-location">{{ calc.city }} {{ calc.address }}</span>
                    }
                  </label>
                </div>
              }

              @if (calculationsList().length === 0) {
                <p class="no-calculations">Keine Berechnungen vorhanden</p>
              }
            </div>

            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input 
                  type="checkbox" 
                  [checked]="includeHelpTexts()"
                  (change)="includeHelpTexts.set($any($event.target).checked)"
                />
                <span>Hilfetexte einschließen</span>
              </label>
              <p class="help-hint">
                Hilfetexte werden am Ende des PDFs eingefügt.
              </p>
            </div>
          </div>
          
          <div class="dialog-footer">
            <button class="btn btn-secondary" (click)="close()">Abbrechen</button>
            <button 
              class="btn btn-primary" 
              (click)="export()"
              [disabled]="selectedCount() < 2 || isExporting()"
            >
              {{ isExporting() ? 'Wird exportiert...' : 'PDF exportieren' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .dialog-content {
      background: white;
      border-radius: 8px;
      padding: 0;
      width: 90%;
      max-width: 600px;
      max-height: 80vh;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid #e5e7eb;
    }

    .dialog-header h2 {
      margin: 0;
      font-size: 18px;
      color: #1f2937;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #6b7280;
      padding: 0;
      width: 30px;
      height: 30px;
    }

    .dialog-body {
      padding: 20px;
      overflow-y: auto;
      flex: 1;
    }

    .info-text {
      margin: 0 0 15px 0;
      color: #6b7280;
      font-size: 14px;
    }

    .calculations-list {
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      margin-bottom: 20px;
      max-height: 300px;
      overflow-y: auto;
    }

    .calc-item {
      padding: 12px;
      border-bottom: 1px solid #f3f4f6;
    }

    .calc-item:last-child {
      border-bottom: none;
    }

    .checkbox-label {
      display: flex;
      align-items: flex-start;
      cursor: pointer;
      font-size: 14px;
      color: #1f2937;
      gap: 10px;
    }

    .checkbox-label input {
      margin-top: 2px;
      cursor: pointer;
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    .checkbox-label input:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .calc-name {
      font-weight: 500;
    }

    .calc-location {
      font-size: 12px;
      color: #9ca3af;
      margin-left: auto;
    }

    .checkbox-group {
      margin: 0;
      margin-left: 12px;
    }

    .help-hint {
      margin: 8px 0 0 33px;
      font-size: 12px;
      color: #9ca3af;
    }

    .no-calculations {
      padding: 20px;
      text-align: center;
      color: #9ca3af;
      font-size: 14px;
    }

    .dialog-footer {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      padding: 15px 20px;
      border-top: 1px solid #e5e7eb;
    }

    .btn {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
      font-size: 14px;
    }

    .btn-primary {
      background: #5445a6;
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background: #3d3280;
    }

    .btn-primary:disabled {
      background: #c4b5fd;
      cursor: not-allowed;
    }

    .btn-secondary {
      background: #e5e7eb;
      color: #1f2937;
    }

    .btn-secondary:hover {
      background: #d1d5db;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportComparisonDialogComponent {
  isOpen = signal(false);
  includeHelpTexts = signal(false);
  isExporting = signal(false);
  calculationsList = signal<CalculationListItem[]>([]);
  selectedIds = signal<Set<number>>(new Set());
  onToast = signal<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  
  private calcStorageService = inject(CalculationStorageService);
  private pdfExportService = inject(CalculationPdfExportService);
  private subscriptionService = inject(SubscriptionService);

  selectedCount = signal(0);

  open(): void {
    // Load saved calculations
    this.calcStorageService.getMyCalculations().subscribe({
      next: (calculations: CalculationListItem[]) => {
        this.calculationsList.set(calculations);
        this.selectedIds.set(new Set());
        this.selectedCount.set(0);
        this.isOpen.set(true);
      },
      error: (error: any) => {
        console.error('Failed to load calculations:', error);
      }
    });
  }

  close(): void {
    this.isOpen.set(false);
    this.isExporting.set(false);
  }

  isSelected(calcId: number): boolean {
    return this.selectedIds().has(calcId);
  }

  isAtMaxSelection(): boolean {
    return this.selectedIds().size >= 3;
  }

  toggleSelection(calcId: number): void {
    const selected = new Set(this.selectedIds());
    
    if (selected.has(calcId)) {
      selected.delete(calcId);
    } else if (selected.size < 3) {
      selected.add(calcId);
    }
    
    this.selectedIds.set(selected);
    this.selectedCount.set(selected.size);
  }

  export(): void {
    const ids = Array.from(this.selectedIds());
    
    if (ids.length === 0) return;

    // Feature Check
    if (!this.subscriptionService.hasFeature('PDF_EXPORT')) {
      this.onToast.set({ message: 'Diese Funktion ist in deinem aktuellen Abonnement nicht verfügbar. Aktualisiere dein Abonnement, um diese Funktion freizuschalten.', type: 'warning' });
      return;
    }

    this.isExporting.set(true);

    this.pdfExportService
      .exportComparisonPdf(ids, this.includeHelpTexts())
      .subscribe({
        next: (blob) => {
          if (blob.size === 0) {
            console.error('PDF blob is empty');
            alert('Fehler: PDF konnte nicht erstellt werden (leer)');
            this.isExporting.set(false);
            return;
          }
          const now = new Date();
          const dateStr = now.toISOString().slice(0, 10);
          const timeStr = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
          const fileName = `Kalkulation_Vergleich_${dateStr}_${timeStr}.pdf`;
          this.pdfExportService.downloadFile(blob, fileName);
          this.close();
        },
        error: (error) => {
          console.error('PDF export failed:', error);
          const errorMsg = error?.error?.message || error?.message || 'Fehler beim PDF-Export';
          alert(`Fehler: ${errorMsg}`);
          this.isExporting.set(false);
        }
      });
  }
}
